import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, Search, TestTube, Clock, RefreshCw } from 'lucide-react';
import { toast } from "sonner";

interface PrescriptionItem {
  id: number;
  product_name: string;
  prescription_group_id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  next_reminder_date: string | null;
  days_until_reminder: number | null;
  status: string;
}

export default function TestTools() {
  const [prescriptions, setPrescriptions] = useState<PrescriptionItem[]>([]);
  const [filteredPrescriptions, setFilteredPrescriptions] = useState<PrescriptionItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [filter, setFilter] = useState<'all' | 'overdue' | 'upcoming'>('all');

  const fetchPrescriptions = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/prescriptions?expand=user', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch prescriptions');
      }
      
      const data = await response.json();
      
      // Process data to add user information and days_until_reminder
      const processedData = data.map((item: any) => {
        const nextReminderDate = item.next_reminder_date ? new Date(item.next_reminder_date) : null;
        const today = new Date();
        
        // Calculate days until reminder
        const daysUntilReminder = nextReminderDate 
          ? Math.ceil((nextReminderDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)) 
          : null;
        
        return {
          ...item,
          days_until_reminder: daysUntilReminder,
          user_name: item.first_name && item.last_name ? 
            `${item.first_name} ${item.last_name}` : 
            (item.user ? `${item.user.first_name} ${item.user.last_name}` : 'Unknown'),
          user_email: item.email || (item.user ? item.user.email : 'N/A')
        };
      });
      
      setPrescriptions(processedData);
      applyFilters(processedData, searchTerm, filter);
      
    } catch (error) {
      console.error('Error fetching prescriptions:', error);
      toast.error('Failed to load prescriptions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrescriptions();
  }, []);

  useEffect(() => {
    applyFilters(prescriptions, searchTerm, filter);
  }, [searchTerm, filter]);

  const applyFilters = (data: PrescriptionItem[], search: string, filterType: string) => {
    let filtered = [...data];
    
    // Apply search filter
    if (search) {
      const lowerSearch = search.toLowerCase();
      filtered = filtered.filter(
        p => p.product_name.toLowerCase().includes(lowerSearch) ||
             p.user_name.toLowerCase().includes(lowerSearch) ||
             p.user_email.toLowerCase().includes(lowerSearch)
      );
    }
    
    // Apply status filter
    if (filterType === 'overdue') {
      filtered = filtered.filter(p => p.days_until_reminder !== null && p.days_until_reminder <= 0);
    } else if (filterType === 'upcoming') {
      filtered = filtered.filter(p => p.days_until_reminder !== null && p.days_until_reminder > 0 && p.days_until_reminder <= 7);
    }
    
    setFilteredPrescriptions(filtered);
  };

  const updateReminderDate = async (id: number, date: Date) => {
    setUpdatingId(id);
    try {
      const response = await fetch(`/api/admin/prescriptions/${id}/reminder-date`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          next_reminder_date: date.toISOString()
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update reminder date');
      }
      
      toast.success('Reminder date updated successfully');
      
      // Refresh the list
      fetchPrescriptions();
      
    } catch (error) {
      console.error('Error updating reminder date:', error);
      toast.error('Failed to update reminder date');
    } finally {
      setUpdatingId(null);
      setSelectedDate(null);
    }
  };

  // Function to get a specific future date (e.g. tomorrow, in 3 days)
  const getFutureDate = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date;
  };

  // Function to format the date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not set';
    try {
      return format(new Date(dateString), 'PPP p');
    } catch {
      return 'Invalid date';
    }
  };
  
  // Get badge style based on days until reminder
  const getReminderBadge = (days: number | null) => {
    if (days === null) return { variant: 'outline', text: 'Not set' };
    if (days <= 0) return { variant: 'destructive', text: 'Overdue' };
    if (days <= 3) return { variant: 'destructive', text: 'Very Soon' };
    if (days <= 7) return { variant: 'warning', text: 'Soon' };
    return { variant: 'secondary', text: `In ${days} days` };
  };

  return (
    <div className="p-6">
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center">
            <TestTube className="mr-2 h-5 w-5" />
            Prescription Testing Tools
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">Description</h3>
            <p className="text-muted-foreground">
              This tool allows you to modify prescription reminder dates for testing purposes. 
              You can set specific dates to trigger reminder emails and test the renewal flow.
            </p>
            <div className="p-4 mt-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="font-medium text-amber-800">Important Notes:</p>
              <ul className="list-disc ml-6 mt-2 text-amber-700 text-sm">
                <li>These changes affect real data and email notifications</li>
                <li>The reminder service runs every hour</li>
                <li>Setting a date to today or yesterday will trigger a notification during the next run</li>
                <li>Use this only for testing purposes</li>
              </ul>
            </div>
          </div>

          <div className="mb-6 flex flex-wrap gap-4">
            <div className="grow">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by product or user..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant={filter === 'all' ? 'default' : 'outline'} 
                onClick={() => setFilter('all')}
              >
                All
              </Button>
              <Button 
                variant={filter === 'overdue' ? 'default' : 'outline'} 
                onClick={() => setFilter('overdue')}
              >
                Overdue
              </Button>
              <Button 
                variant={filter === 'upcoming' ? 'default' : 'outline'} 
                onClick={() => setFilter('upcoming')}
              >
                Upcoming
              </Button>
              <Button 
                variant="outline" 
                onClick={fetchPrescriptions}
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Refresh
              </Button>
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Current Reminder</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center">
                      Loading prescriptions...
                    </TableCell>
                  </TableRow>
                ) : filteredPrescriptions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center">
                      No prescriptions found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPrescriptions.map((prescription) => {
                    const reminderBadge = getReminderBadge(prescription.days_until_reminder);
                    
                    return (
                      <TableRow key={prescription.id}>
                        <TableCell>
                          <div className="font-medium">{prescription.product_name}</div>
                          <div className="text-sm text-muted-foreground">ID: {prescription.id}</div>
                        </TableCell>
                        <TableCell>
                          <div>{prescription.user_name}</div>
                          <div className="text-sm text-muted-foreground">{prescription.user_email}</div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-1 text-muted-foreground" />
                            <span>{formatDate(prescription.next_reminder_date)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={reminderBadge.variant as any}>
                            {reminderBadge.text}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {/* Quick actions */}
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => updateReminderDate(prescription.id, new Date())}
                              disabled={updatingId === prescription.id}
                            >
                              Today
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => updateReminderDate(prescription.id, getFutureDate(1))}
                              disabled={updatingId === prescription.id}
                            >
                              Tomorrow
                            </Button>
                            
                            {/* Date picker */}
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  disabled={updatingId === prescription.id}
                                >
                                  <CalendarIcon className="h-4 w-4 mr-1" />
                                  Custom
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0">
                                <Calendar
                                  mode="single"
                                  selected={selectedDate || undefined}
                                  onSelect={(date: Date | undefined) => {
                                    if (date) {
                                      setSelectedDate(date);
                                      updateReminderDate(prescription.id, date);
                                    }
                                  }}
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}